/**
 * Admin App Providers
 *
 * Uses RainbowKit + wagmi for external wallet connection (MetaMask, etc.)
 * and Convex for backend queries.
 */

import { getChainConfig, isSupportedChainId, type SupportedChainId } from "@breakout/sdk"
import "@rainbow-me/rainbowkit/styles.css"
import { ConvexQueryClient } from "@convex-dev/react-query"
import { connectorsForWallets, RainbowKitProvider } from "@rainbow-me/rainbowkit"
import { injectedWallet, metaMaskWallet, safeWallet } from "@rainbow-me/rainbowkit/wallets"
import { QueryClient, QueryClientProvider } from "@tanstack/react-query"
import { ReactQueryDevtools } from "@tanstack/react-query-devtools"
import { ConvexProvider, ConvexReactClient } from "convex/react"
import type { ReactNode } from "react"
import { createContext, useContext, useMemo } from "react"
import type { PublicClient } from "viem"
import { createPublicClient, http as viemHttp } from "viem"
import { createConfig, http, WagmiProvider } from "wagmi"
import { base, baseSepolia, foundry } from "wagmi/chains"
import { ExecutionModeProvider } from "../hooks/useExecutionMode"
import { VITE_CHAIN_ID, VITE_CONVEX_URL, VITE_RPC_URL } from "./env"

// =============================================================================
// Chain Configuration
// =============================================================================

const rawChainId = Number.parseInt(VITE_CHAIN_ID, 10)
if (!isSupportedChainId(rawChainId)) {
	throw new Error(`Unsupported chain ID: ${rawChainId}`)
}
const chainId: SupportedChainId = rawChainId
// SDK chains.ts is updated by e2e-setup.sh with deployed addresses
const sdkChainConfig = getChainConfig(chainId)

function getChain() {
	switch (chainId) {
		case 8453:
			return base
		case 84532:
			return baseSepolia
		case 31337:
			return foundry
		default:
			return baseSepolia
	}
}

const chain = getChain()

// =============================================================================
// Wagmi + RainbowKit Configuration
// =============================================================================

// Note: wagmiConfig is defined after rpcUrl at the end of this file
// This allows it to use the properly configured RPC URL

// =============================================================================
// Convex Client
// =============================================================================

const convexClient = new ConvexReactClient(VITE_CONVEX_URL)
const convexQueryClient = new ConvexQueryClient(convexClient)

// =============================================================================
// Query Client
// =============================================================================

const queryClient = new QueryClient({
	defaultOptions: {
		queries: {
			queryKeyHashFn: convexQueryClient.hashFn(),
			queryFn: convexQueryClient.queryFn(),
			staleTime: 30_000,
			gcTime: 10 * 60 * 1000, // 10 minutes - keep unused data longer for admin workflows
			retry: (failureCount, error) => {
				// Don't retry auth errors
				if (error instanceof Error && error.message.toLowerCase().includes("auth")) {
					return false
				}
				return failureCount < 3
			},
			refetchOnWindowFocus: false, // Admin doesn't need aggressive refetching
		},
	},
})
convexQueryClient.connect(queryClient)

// =============================================================================
// RPC Configuration
// =============================================================================

function getRpcUrl() {
	if (VITE_RPC_URL) {
		return VITE_RPC_URL
	}
	switch (chainId) {
		case 8453:
			return "https://mainnet.base.org"
		case 84532:
			return "https://sepolia.base.org"
		case 31337:
			return "http://localhost:8545"
		default:
			return "https://sepolia.base.org"
	}
}

const rpcUrl = getRpcUrl()

// =============================================================================
// Wagmi Configuration (defined here after rpcUrl is available)
// =============================================================================

// Configure wallets for RainbowKit - includes Safe for multisig support
const connectors = connectorsForWallets(
	[
		{
			groupName: "Recommended",
			wallets: [safeWallet, metaMaskWallet, injectedWallet],
		},
	],
	{
		appName: "Breakout Admin",
		projectId: "breakout-admin", // WalletConnect project ID (optional for Safe)
	},
)

const wagmiConfig = createConfig({
	chains: [chain] as const,
	connectors,
	transports: {
		[chain.id]: http(rpcUrl),
	} as Record<typeof chain.id, ReturnType<typeof http>>,
})

// =============================================================================
// Public Client (singleton for all SDK operations)
// =============================================================================

const publicClient = createPublicClient({
	chain,
	transport: viemHttp(rpcUrl, {
		retryCount: 3,
		retryDelay: 150,
		timeout: 5_000, // 5s timeout (reduced from 10s default for faster failure)
	}),
	batch: {
		multicall: { batchSize: 256, wait: 10 },
	},
})

// =============================================================================
// Admin Config Context
// =============================================================================

interface AdminConfig {
	chainId: SupportedChainId
	chainConfig: typeof sdkChainConfig
	convexClient: typeof convexClient
	chain: typeof chain
	rpcUrl: string
	/** Centralized public client for SDK operations */
	publicClient: PublicClient
}

const AdminConfigContext = createContext<AdminConfig | null>(null)

export function useAdminConfig(): AdminConfig {
	const context = useContext(AdminConfigContext)
	if (!context) {
		throw new Error("useAdminConfig must be used within Providers")
	}
	return context
}

// =============================================================================
// Providers
// =============================================================================

export function Providers({ children }: { children: ReactNode }) {
	const adminConfig = useMemo<AdminConfig>(
		() => ({
			chainId,
			chainConfig: sdkChainConfig,
			convexClient,
			chain,
			rpcUrl,
			publicClient: publicClient as PublicClient,
		}),
		[],
	)

	return (
		<ConvexProvider client={convexClient}>
			<WagmiProvider config={wagmiConfig}>
				<QueryClientProvider client={queryClient}>
					<RainbowKitProvider>
						<ExecutionModeProvider>
							<AdminConfigContext.Provider value={adminConfig}>
								{children}
							</AdminConfigContext.Provider>
						</ExecutionModeProvider>
					</RainbowKitProvider>
					<ReactQueryDevtools buttonPosition="bottom-left" initialIsOpen={false} />
				</QueryClientProvider>
			</WagmiProvider>
		</ConvexProvider>
	)
}

export { chain, sdkChainConfig as chainConfig }
